import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {SortDescriptor} from '@progress/kendo-data-query';
import {
    RankedRule,
    RuleDefinition,
    RuleDefinitionBucket,
    RuleDefinitionJourney,
    VOLUME_TRIGGER_OPTIONS
} from '../business-rules.model';
import {NavigateWithFiltersService} from '../../../shared/navigate-with-filters.service';
import {BusinessRulesService} from '../business-rules.service';
import {UrlStore} from '../../../shared/url-store';
import {DateUtils} from '../../../shared/date-utils';

const MAX_VISIBLE_RSIDS = 3;
const NO_VOLUME_TRIGGER = 'None';

@Component({
    selector: 'app-rule-detail-dialog',
    templateUrl: './rule-detail-dialog.component.html',
    styleUrls: ['./rule-detail-dialog.component.scss']
})
export class RuleDetailDialogComponent implements OnInit {
    rules: RuleDefinition[] = [];
    loading = true;
    error: string | null = null;

    /* Sort state per rule index: one dialog can show several rules, each with its
       own pair of grids, and sorting one must not reorder another. */
    bucketSorts: { [ruleIndex: number]: SortDescriptor[] } = {};
    journeySorts: { [ruleIndex: number]: SortDescriptor[] } = {};

    /* Same lookup the editable bucket rules grid uses; buckets are price points,
       so the Bucket column sorts by the configured bucket order, not the code. */
    private allBucketData: any[] = [];

    /* Rules that applied to this journey but were beaten on priority (RLP-4261).
       Defaults to empty: the business rule prioritization screen also opens this dialog
       and passes none, and must keep rendering exactly as before. */
    unappliedRules: RankedRule[] = [];
    unappliedLoading = false;
    unappliedError: string | null = null;

    /* Comma-separated names of Max_Alloc rules that won on priority but were nullified by
       a bucket-order conflict. A different reason from being beaten on priority, and it
       comes from the pipeline rather than the live contest, so it is shown separately. */
    nullifiedRuleNames = '';

    /* The inventory grid owns the DialogRef, so ask it to close before we navigate away. */
    @Output() closeDialog = new EventEmitter<void>();

    constructor(private navService: NavigateWithFiltersService,
                private businessRulesSvc: BusinessRulesService) {
    }

    ngOnInit(): void {
        this.businessRulesSvc.getBuckets().subscribe(results => {
            this.allBucketData = results || [];
        }, error => {
            // The code-based fallback sort still works without the lookup.
            console.error(`Error retrieving bucket data ${error}`);
        });
    }

    onBucketSortChange(rule: RuleDefinition, ruleIndex: number, sort: SortDescriptor[]): void {
        this.bucketSorts[ruleIndex] = sort;
        const descriptor = this.activeSort(sort);
        if (!descriptor) {
            return;
        }
        rule.bucket_rules = this.sorted(rule.bucket_rules, descriptor,
            (row: RuleDefinitionBucket, field: string) => row[field],
            descriptor.field === 'bucketCode'
                ? (a: RuleDefinitionBucket, b: RuleDefinitionBucket) => this.compareByBucketOrder(a, b)
                : null);
    }

    onJourneySortChange(rule: RuleDefinition, ruleIndex: number, sort: SortDescriptor[]): void {
        this.journeySorts[ruleIndex] = sort;
        const descriptor = this.activeSort(sort);
        if (!descriptor) {
            return;
        }
        rule.journey_rules = this.sorted(rule.journey_rules, descriptor,
            (row: RuleDefinitionJourney, field: string) => this.journeySortValue(row, field));
    }

    /* A third header click clears the direction; leave the rows alone then, the
       same way the editable grids do. */
    private activeSort(sort: SortDescriptor[]): SortDescriptor | null {
        return sort && sort.length === 1 && sort[0].field && sort[0].dir ? sort[0] : null;
    }

    private sorted<T>(rows: T[], descriptor: SortDescriptor,
                      valueOf: (row: T, field: string) => any,
                      compare: ((a: T, b: T) => number) | null = null): T[] {
        const comparer = compare
            || ((a: T, b: T) => this.compareValues(valueOf(a, descriptor.field), valueOf(b, descriptor.field)));
        // A new array, not an in-place sort: the grid re-renders on a rebinding.
        return [...rows].sort((a, b) => descriptor.dir === 'desc' ? -comparer(a, b) : comparer(a, b));
    }

    /* Columns that render through a template sort by what they display, so the
       order on screen never contradicts the header arrow. */
    private journeySortValue(row: RuleDefinitionJourney, field: string): any {
        switch (field) {
            case 'rsid':
                return this.getRsidTooltip(row.rsid);
            case 'volumeTrigger':
                return this.volumeTriggerLabel(row.volumeTrigger);
            case 'triggerLegOrigin':
                return this.triggerLegOriginLabel(row);
            case 'triggerLegDestination':
                return this.triggerLegDestinationLabel(row);
            default:
                return row[field];
        }
    }

    private compareValues(a: any, b: any): number {
        if (a === null || a === undefined) {
            return (b === null || b === undefined) ? 0 : 1;
        }
        if (b === null || b === undefined) {
            return -1;
        }
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }
        return String(a).localeCompare(String(b), undefined, {numeric: true, sensitivity: 'base'});
    }

    /* Mirrors BucketRulesGridComponent.compareByBucketOrder so the read-only
       dialog orders buckets the same way as the editable grid. */
    private compareByBucketOrder(a: RuleDefinitionBucket, b: RuleDefinitionBucket): number {
        const aOrder = this.bucketOrder(a.bucketCode);
        const bOrder = this.bucketOrder(b.bucketCode);
        if (aOrder !== null && bOrder !== null) {
            return aOrder - bOrder;
        }
        /* Neither bucket has a configured order -- the lookup has not loaded, or
           these codes are no longer in it -- so fall back to the code so the
           column still sorts instead of appearing to ignore the click. */
        if (aOrder === null && bOrder === null) {
            return String(a.bucketCode || '').localeCompare(
                String(b.bucketCode || ''), undefined, {numeric: true, sensitivity: 'base'});
        }
        // One side has a price position and the other does not; put it first.
        return aOrder === null ? 1 : -1;
    }

    private bucketOrder(bucketCode: string): number | null {
        const order = this.allBucketData.find(x => x.bucketCode === bucketCode)?.bucketOrder;
        return order === null || order === undefined ? null : order;
    }

    /* One button per rule type, not one per dialog: the prioritization screen accepts
       exactly one ruleType and a journey's suppressed rules can span Min and Max. */
    reprioritizeRuleTypes(): string[] {
        return this.unappliedRules
            .map(rule => rule.ruleType)
            .filter((ruleType, index, all) => all.indexOf(ruleType) === index);
    }

    onReprioritize(ruleType: string): void {
        const rule = this.unappliedRules.find(r => r.ruleType === ruleType);
        if (!rule?.deepLink) {
            return;
        }
        const link = rule.deepLink;
        this.navService.navigateWithFilters(
            [UrlStore.ui.businessRules, 'business-rules-priorities'],
            {
                rsid: link.rsid,
                origin: link.origin,
                dest: link.destination,
                // Parsed as local midnight. new Date('2026-08-22') would be UTC midnight,
                // which is the previous calendar day at any negative UTC offset.
                startDate: DateUtils.convertToDate(link.startDate),
                endDate: DateUtils.convertToDate(link.endDate),
                ruleType: link.ruleType,
                passengerClass: link.passengerClass,
                journeyType: link.journeyType ? [link.journeyType] : null
            }
        );
        this.closeDialog.emit();
    }

    suppressedByLabel(rule: RankedRule): string {
        if (!rule.suppressedBy) {
            return '';
        }
        const overridden = rule.suppressedBy.priorityIsOverridden ? ' (overridden)' : '';
        return `${rule.suppressedBy.ruleName} @ ${rule.suppressedBy.effectivePriority}${overridden}`;
    }

    getRsidDisplay(rsids: string[]): string {
        if (!rsids || rsids.length === 0) {
            return '';
        }
        if (rsids.length <= MAX_VISIBLE_RSIDS) {
            return rsids.join(', ');
        }
        return rsids.slice(0, MAX_VISIBLE_RSIDS).join(', ') + ` (+${rsids.length - MAX_VISIBLE_RSIDS} more)`;
    }

    hasOverflowRsids(rsids: string[]): boolean {
        return rsids && rsids.length > MAX_VISIBLE_RSIDS;
    }

    getRsidTooltip(rsids: string[]): string {
        if (!rsids) {
            return '';
        }
        return rsids.join(', ');
    }

    /* Same visibility flag the editable Journey Rules grid uses, cached to
       localStorage by the business rules screen. */
    volumeTriggerVisible(): boolean {
        return !!parseInt(localStorage.getItem('volumeTriggerVisibility'), 10);
    }

    volumeTriggerLabel(volumeTrigger: string): string {
        return VOLUME_TRIGGER_OPTIONS.find(x => x.id === (volumeTrigger || NO_VOLUME_TRIGGER))?.name;
    }

    /* Only the two constrained triggers compare a load factor, so only they carry
       a % -- the selling ones are a ticket count. */
    volumeFactorDisplay(journeyRule: RuleDefinitionJourney): string {
        const volumeFactor = journeyRule?.volumeFactor;
        if (volumeFactor === null || volumeFactor === undefined) {
            return '';
        }
        const isPercentage = journeyRule?.volumeTrigger === 'Under_Pct'
            || journeyRule?.volumeTrigger === 'Over_Pct';
        return isPercentage ? `${volumeFactor}%` : `${volumeFactor}`;
    }

    triggerLegOriginLabel(journeyRule: RuleDefinitionJourney): string {
        return this.triggerLegLabel(journeyRule, journeyRule?.triggerLegOrigin);
    }

    triggerLegDestinationLabel(journeyRule: RuleDefinitionJourney): string {
        return this.triggerLegLabel(journeyRule, journeyRule?.triggerLegDestination);
    }

    /* Mirrors the editable grid: each column shows only its own station code, and
       a row with no trigger shows nothing at all rather than implying a leg
       governs something. */
    private triggerLegLabel(journeyRule: RuleDefinitionJourney, stationCode: string): string {
        const volumeTrigger = journeyRule?.volumeTrigger;
        if (!volumeTrigger || volumeTrigger === NO_VOLUME_TRIGGER) {
            return '';
        }
        return stationCode || 'Critical Leg';
    }
}
